import { rateLimitedRpcFetch } from './rateLimitedFetch';

/**
 * Coalesces individual JSON-RPC requests into JSON-RPC batch arrays.
 *
 * RPC providers rate-limit by HTTP request, not by JSON-RPC call — so the
 * page-load burst (~15 near-simultaneous single calls from independent
 * subsystems) is structurally expensive. This transport collects single
 * JSON-RPC calls to the same endpoint inside a short window and sends them
 * as one batch request, turning the burst into 1-2 HTTP requests.
 *
 * Sits above rateLimitedRpcFetch: each flushed batch (or passed-through
 * request) still respects the app-wide concurrency/spacing limits.
 */

// How long to hold the first request of a batch before flushing. Small enough
// to be imperceptible, large enough to catch a same-tick burst.
const BATCH_WINDOW_MS = 25;
// Flush early once this many calls are queued (providers commonly cap
// batch sizes; stay conservative).
const MAX_BATCH_SIZE = 10;
// Only these methods are batched. Helius routes batches containing certain
// methods (getProgramAccounts, getTokenAccountsByOwner, ...) to a backend
// with a reduced method set, which then rejects other calls in the same
// batch with -32601 "Method not found". These four are empirically safe on
// both backends — and they're the high-volume methods, so batching them
// captures most of the benefit. Everything else dispatches individually.
const BATCHABLE_METHODS = new Set([
	'getAccountInfo',
	'getMultipleAccounts',
	'getSlot',
	'getBalance',
]);

type JsonRpcCall = {
	jsonrpc: string;
	id: number | string;
	method: string;
	params?: unknown[];
};

type PendingCall = {
	call: JsonRpcCall;
	init: RequestInit;
	resolve: (response: Response) => void;
	reject: (error: unknown) => void;
};

type Queue = {
	pending: PendingCall[];
	timer: ReturnType<typeof setTimeout> | null;
};

const queuesByUrl = new Map<string, Queue>();
let nextBatchId = 1;

const jsonResponse = (payload: unknown): Response =>
	new Response(JSON.stringify(payload), {
		status: 200,
		headers: { 'content-type': 'application/json' },
	});

const flushQueue = async (url: string) => {
	const queue = queuesByUrl.get(url);
	if (!queue || queue.pending.length === 0) return;

	if (queue.timer) {
		clearTimeout(queue.timer);
		queue.timer = null;
	}
	const batch = queue.pending.splice(0, queue.pending.length);

	// Single queued call — send it unchanged so the wire format is identical
	// to an un-batched request.
	if (batch.length === 1) {
		const { call, init, resolve, reject } = batch[0];
		try {
			resolve(
				await rateLimitedRpcFetch(url, {
					...init,
					body: JSON.stringify(call),
				})
			);
		} catch (error) {
			reject(error);
		}
		return;
	}

	// Ids from different Connection instances can collide within one batch —
	// rewrite them to batch-unique ids and restore on the way back out.
	// Key by string so matching survives a server that echoes ids as strings
	// (JSON-RPC allows either type); a type mismatch would otherwise silently
	// classify a valid result as "missing" and hand the caller a -32603.
	const idMap = new Map<string, PendingCall>();
	const batchBody = batch.map((pendingCall) => {
		const batchId = nextBatchId++;
		idMap.set(String(batchId), pendingCall);
		return { ...pendingCall.call, id: batchId };
	});

	try {
		const response = await rateLimitedRpcFetch(url, {
			...batch[0].init,
			body: JSON.stringify(batchBody),
		});

		if (!response.ok) {
			// Hand every caller an equivalent error response so their own retry
			// logic sees exactly what a standalone request would have seen.
			const errorBody = await response.text();
			batch.forEach(({ resolve }) => {
				resolve(
					new Response(errorBody, {
						status: response.status,
						statusText: response.statusText,
						headers: response.headers,
					})
				);
			});
			return;
		}

		const results = await response.json();
		const resultsArray: { id: number | string }[] = Array.isArray(results)
			? results
			: [results];

		const unresolved = new Set(batch);
		for (const result of resultsArray) {
			const pendingCall = idMap.get(String(result.id));
			if (!pendingCall) continue;
			unresolved.delete(pendingCall);
			pendingCall.resolve(jsonResponse({ ...result, id: pendingCall.call.id }));
		}

		// Anything the server didn't answer gets a JSON-RPC error so callers
		// fail loudly instead of hanging.
		unresolved.forEach((pendingCall) => {
			pendingCall.resolve(
				jsonResponse({
					jsonrpc: '2.0',
					id: pendingCall.call.id,
					error: {
						code: -32603,
						message: 'Batched RPC request received no response for this id',
					},
				})
			);
		});
	} catch (error) {
		batch.forEach(({ reject }) => reject(error));
	}
};

const parseSingleJsonRpcCall = (body: unknown): JsonRpcCall | null => {
	if (typeof body !== 'string' || !body.includes('"jsonrpc"')) return null;
	try {
		const parsed = JSON.parse(body);
		// Arrays are already batches (e.g. bulkAccountLoader) — pass through.
		if (
			Array.isArray(parsed) ||
			typeof parsed !== 'object' ||
			parsed === null ||
			typeof parsed.method !== 'string' ||
			parsed.id === undefined
		) {
			return null;
		}
		return parsed as JsonRpcCall;
	} catch {
		return null;
	}
};

export const batchedRpcFetch: typeof fetch = (input, init) => {
	const call =
		typeof input === 'string' || input instanceof URL
			? parseSingleJsonRpcCall(init?.body)
			: null;

	if (!call || !BATCHABLE_METHODS.has(call.method)) {
		return rateLimitedRpcFetch(input, init);
	}

	const url = input.toString();
	let queue = queuesByUrl.get(url);
	if (!queue) {
		queue = { pending: [], timer: null };
		queuesByUrl.set(url, queue);
	}

	return new Promise<Response>((resolve, reject) => {
		queue.pending.push({ call, init: init ?? {}, resolve, reject });

		if (queue.pending.length >= MAX_BATCH_SIZE) {
			flushQueue(url);
		} else if (!queue.timer) {
			queue.timer = setTimeout(() => {
				queue.timer = null;
				flushQueue(url);
			}, BATCH_WINDOW_MS);
		}
	});
};
